import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { StreetInfoService, SearchApiResponseItems } from '../../shared/services/street-info.service';
import { ChartConfiguration, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { timer, from, switchMap, Subscription, forkJoin } from 'rxjs';
import { MatTabsModule } from '@angular/material/tabs';
import { FormsModule } from '@angular/forms';

declare var google: any;

interface Street extends SearchApiResponseItems {
  polyline: google.maps.Polyline;
  uniqueId?: string;
  traffic?: string;
  trafficDescription?: string;
  additionalTimeInSeconds?: number;
  additionalTime?: string;
  isVisible?: boolean;
  trafficJamLengthText?: string;
  trafficJamMarkersTexts?: string[];
  trafficJamPaths?: any[];
}

@Component({
  selector: 'app-info-panel',
  imports: [
    MatCardModule,
    MatExpansionModule,
    MatDividerModule,
    MatIconModule,
    MatChipsModule,
    CommonModule,
    BaseChartDirective,
    MatTabsModule,
    FormsModule
  ],
  templateUrl: './info-panel.html',
  styleUrl: './info-panel.scss',
  standalone: true
})
export class InfoPanel implements OnInit {
  readonly isLoading = signal(false); // Visualizzazione spinner in fase di caricamento dati
  readonly pannelloAperto = signal(true);
  readonly fetchDataCompleted = signal<any>(null);
  readonly fetchSelectedStreetDataCompleted = signal<any>(null);
  readonly selectedStreet = signal<Street | null>(null);
  readonly activeTabIndex = signal(0);  // 0 = Monitoraggio, 1 = Analisi
  readonly showDisruptions = signal(true);  // Stato visibilità delle interruzioni stradali

  public streets: Array<any> = [];
  public visibleStreets: Array<any> = [];
  private googleMap: any = null;
  private streetViewPanorama: any = null;
  private originalBounds: any = null;
  private readonly updateRealTimeTrafficDataInterval = 120000;  // 2 minuti

  private readonly defaultPolylineStrokeColor: string = "#06c";
  private readonly defaultPolylineStrokeOpacity: number = 1;
  private readonly defaultPolylineStrokeWeight: number = 3;
  private readonly defaultSelectedPolylineStrokeWeight: number = 4;

  private readonly color_normal: string = "#15D68F";               // Verde
  public trafficDescription_normal: string = "scorrevole";
  private readonly color_slow: string = "#FFCF43";                 // Giallo
  public trafficDescription_slow: string = "medio";
  private readonly color_trafficJam: string = "#A82826";           // Rosso
  public trafficDescription_trafficJam: string = "intenso";

  private readonly polylinesArray: Array<google.maps.Polyline> = [];
  private readonly trafficMarkers: Array<any> = []; // Array marker di segnalazione del traffico intenso
  private globalTrafficSegmentsPolylines: Array<google.maps.Polyline> = []; // Array contenente i segmenti visibili nel tab "Monitoraggio del traffico"
  private readonly disruptionMarkers: Array<any> = [];  // Array per i marker delle interruzioni stradali
  private readonly disruptionPolylines: Array<google.maps.Polyline> = []; // Array tratti stradali con interruzioni
  private isFirstTrafficLoad: boolean = true;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  public lineChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [{
      data: [],
      label: 'Tempo medio (statico)',
      borderColor: 'rgb(146, 52, 234)',
      backgroundColor: 'rgba(146, 52, 234, 0.1)',
      fill: false,
      tension: 0.3,
      borderWidth: 1
    },
    {
      data: [],
      label: 'Tempo con traffico',
      borderColor: 'rgb(66, 133, 244)',
      backgroundColor: 'rgba(66, 133, 244, 0.1)',
      fill: false,
      tension: 0.3,
      borderWidth: 1
    }]
  };

  public lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false, // Disabilita il rapporto d'aspetto fisso predefinito di Charts.js (2:1)
    scales: {
      y: {
        title: {
          display: true,
          text: 'Minuti'
        },
        beginAtZero: false,
        ticks: {
          callback: (value) => {
            if((value === null) || (value === undefined)) {
              return '';
            }

            // Formattazione etichette
            return this.fromSecondsToMinutes(value as number);
        }
      }
      },
      x: {
        title: {
          display: true,
          text: 'Giorno e ora'
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 7 // Mostra solo 7 etichette distribuite
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        labels: {
          // Filtro sulle voci della legenda (nasconde quelle relative al tempo "Statico")
          filter: (item, chart) => {
            return !item.text.includes('Statico');  // Mostra nella legenda solo le voci che non contengono la parola "Statico"
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            // Formattazione del valore che appare al passaggio del mouse sulla linea
            const label = context.dataset.label || '';
            const value = context.parsed.y ?? 0;

            return `${label}: ${this.fromSecondsToMinutes(value)}`;
          },
          title: (context) => {
            if(context.length > 0) {
              const item = context[0];
              const ds = item.dataset as any;
              const origDateStr = ds.originalDates?.[item.dataIndex];

              if(origDateStr) {
                const d = new Date(origDateStr);
                // Viene utilizzato getUTCDate per ignorare il fuso orario locale del browser e visualizzare esattamente il valore restituito dal servizio in "recordHour"
                const day = d.getUTCDate().toString().padStart(2, '0');
                const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
                const year = d.getUTCFullYear();
                const hours = d.getUTCHours().toString().padStart(2, '0');
                const minutes = d.getUTCMinutes().toString().padStart(2, '0');

                return `${day}/${month}/${year} alle ${hours}:${minutes}`;
              }

              // Se non presente "origDateStr" utilizza l'etichetta base (Giorno X)
              return item.label;
            }

            return '';
          }
        }
      }
    }
  };

  // Plugin utilizzato per distanziare la legenda dal grafico
  public lineChartPlugins = [{
    id: 'legend-margin-bottom',
    beforeInit(chart: any) {
      // Intercetta il calcolo dell'altezza originale della legenda
      const originalFit = chart.legend.fit;
      // Aggiunge uno spazio extra tra legenda e grafico
      chart.legend.fit = function fit() {
        originalFit.bind(chart.legend)();
        this.height += 20;  // Viene introdotto uno spazio di 25px tra legenda e grafico
      };
    }
  }];

  public period1Start: string = "";
  public period2Start: string = "";
  public intervalDays: number = 7;
  public dateOverlapError: string = ""; // Variabile per mostrare un messaggio di errore nel caso in cui i 2 intervalli si sovrappongano

  private trafficSubscription: Subscription | null = null;

  private ettometricaFeatures: any[] = [];

  constructor(
    private readonly streetService: StreetInfoService,
    private readonly cdr: ChangeDetectorRef
  ) {};

  ngOnInit(): void {
    this.isLoading.set(true); // Visualizzazione dello spinner

    const fetchDatiStrade = this.streetService.getData().then((response: any) => {
      if(response?.items) {
        this.streets = response.items;
      }
    }).catch(error => console.error(error));

    this.streetService.map$.subscribe(map => {
      if(map) {
        this.googleMap = map;

        this.streetViewPanorama = map.getStreetView();  // Istanza StreetView collegata alla mappa

        google.maps.event.addListener(map, 'disruptions_toggled', (isVisible: boolean) => {
          this.showDisruptions.set(isVisible);

          const targetMap = isVisible ? this.googleMap : null;

          for(const marker of this.disruptionMarkers) {
            marker.map = targetMap;
          }

          for(const polyline of this.disruptionPolylines) {
            polyline.setMap(targetMap);
          }
        });

        fetchDatiStrade.then(() => {
          if(this.streets.length > 0 && this.polylinesArray.length === 0) {
            this.initializePolylinesAndBounds(this.streets);

            // Timer per il traffico in tempo reale
            this.startTrafficUpdates();
          }
        }).finally(() => {
          this.isLoading.set(false); // Rimozione dello spinner
        });
      }
    });

    // Caricamento in memoria del layer "Ettometrica" per recuperare le informazioni chilometriche (da utilizzare nell'alert delle code)
    fetch('assets/geojson/Ettometrica.geojson')
      .then(res => res.json())
      .then(data => {
        this.ettometricaFeatures = data.features || [];
      })
      .catch(err => console.error("Errore caricamento \"Ettometrica\":", err));
  }

  // Avvia il monitoraggio del traffico chiamando la relativa API
  private startTrafficUpdates(): void {
    if(this.trafficSubscription) {
      return;
    }

    this.trafficSubscription = timer(0, this.updateRealTimeTrafficDataInterval)
    .pipe(switchMap(() => forkJoin({
      traffic: from(this.streetService.getStreetRealTimeTrafficWithGeometries()),
      disruptions: from(this.streetService.getDisruptions())
    })))
    .subscribe({
      next: (response) => {
        // Gestione traffico
        if(response.traffic) {
          this.processTrafficDataWithGeometries(response.traffic);
          this.fetchDataCompleted.set(response.traffic);
        }

        // Gestione interruzioni
        if(response.disruptions) {
          this.drawDisruptionMarkers(response.disruptions);
        }
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  // Interrompe il monitoraggio del traffico fermando le chiamate alla relativa API
  private stopTrafficUpdates(): void {
    if(this.trafficSubscription) {
      this.trafficSubscription.unsubscribe();
      this.trafficSubscription = null;
    }
  }

  private initializePolylinesAndBounds(streets: any[]): void {
    if(!this.originalBounds) {
      this.originalBounds = new google.maps.LatLngBounds();
    }

    for(const street of streets) {
      if(street.geometryConverted?.coordinates) {
        const polyline = new google.maps.Polyline({
          path: street["geometryConverted"]["coordinates"],
          geodesic: true,
          strokeColor: this.defaultPolylineStrokeColor,
          strokeOpacity: this.defaultPolylineStrokeOpacity,
          strokeWeight: this.defaultPolylineStrokeWeight,
          clickable: true,
          zIndex: 1
        });

        street["polyline"] = polyline;

        polyline.setValues({
          data: street
        });

        this.addMouseOverListener(polyline);
        this.addMouseOutListener(polyline);

        this.polylinesArray.push(polyline);

        // Bounds
        const path = polyline.getPath();

        path.forEach((latLng: google.maps.LatLng) => {
          if(latLng.lat() !== 0 && latLng.lng() !== 0) {  // Se non sono presenti le coordinate non estende il bounds
            this.originalBounds.extend(latLng);
          }
        });
      }
    }

    if(this.originalBounds && this.googleMap) {
      this.googleMap.fitBounds(this.originalBounds);
    }
  }

  // Metodo per la gestione della visibilità delle polyline sulla mappa (in base al traffico)
  private updatePolylinesVisibility(preventFitBounds: boolean = false): void {
    if(!this.googleMap) return;

    const isMonitoraggioTab = this.activeTabIndex() === 0;

    // Tab 1: Accendiamo/spegniamo le strade base (azzurre)
    for(const street of this.streets) {
      street.isVisible = !isMonitoraggioTab; // Visibili solo se non siamo in "Monitoraggio del traffico"
      if(street.polyline && !this.selectedStreet()) {
        street.polyline.setMap(street.isVisible ? this.googleMap : null);
      }
    }

    // Tab 0: Accendiamo/spegniamo i segmenti "rossi" (eventi di traffico fusi)
    if(!this.selectedStreet()) {
      this.globalTrafficSegmentsPolylines.forEach(polyline => {
        polyline.setMap(isMonitoraggioTab ? this.googleMap : null);
      });

      // Auto-zoom eseguito solo se non bloccato dal parametro "preventFitBounds"
      if(!preventFitBounds) {
        this.fitBoundsToVisibleStreets();
      }
    }
  }

  // Calcola il perimetro (bounds) di tutte le strade attualmente visibili e adatta lo zoom
  private fitBoundsToVisibleStreets(): void {
    if(!this.googleMap) return;

    let visibleBounds = new google.maps.LatLngBounds();
    let hasVisible = false;

    // Utilizza la lista corretta in base al Tab
    const arrayToUse = this.activeTabIndex() === 0 ? this.visibleStreets : this.streets;

    for(const street of arrayToUse) {
      if(street.polyline) {
        street.polyline.getPath().forEach((latLng: google.maps.LatLng) => {
          if(latLng.lat() !== 0 && latLng.lng() !== 0) {
            visibleBounds.extend(latLng);
            hasVisible = true;
          }
        });
      }
    }

    if(hasVisible) {
      this.googleMap.fitBounds(visibleBounds);
    } else if(this.originalBounds) {
      this.googleMap.fitBounds(this.originalBounds);
    }
  }

  // Reset di tutte le strade alla condizione di "Nessun Traffico" (da richiamare ad ogni chiamata all'API realtime?status=TRAFFIC_JAM)
  // in modo da avere nella lista sempre i dati del traffico aggiornati
  private resetAllStreetsToDefault(): void {
    // Rimuozione dei segmenti di traffico globali dalla mappa
    this.globalTrafficSegmentsPolylines.forEach(polyline => polyline.setMap(null));
    this.globalTrafficSegmentsPolylines = [];

    for(const street of this.streets) {
      // Rimozione di tutte le chiavi aggiunte dalla chiamata del traffico in tempo reale
      delete street.traffic;
      delete street.trafficDescription;
      delete street.additionalTime;
      delete street.additionalTimeInSeconds;
      delete street.trafficJamLengthText;
      delete street.trafficJamMarkersTexts;
      delete street.trafficJamPaths;

      // Ripristina la polyline al colore standard
      if(street.polyline) {
        street.polyline.setOptions({
          strokeColor: this.defaultPolylineStrokeColor,
          strokeOpacity: this.defaultPolylineStrokeOpacity,
          strokeWeight: this.defaultPolylineStrokeWeight
        });
      }
    }
  }

  addMouseOverListener(polyline: google.maps.Polyline) {
    polyline.addListener("mouseover", (event: google.maps.PolyMouseEvent) => {
      this.setSelectedPolylineStile(polyline);
    });
  }

  addMouseOutListener(polyline: google.maps.Polyline) {
    let listener = polyline.addListener("mouseout", (event: google.maps.PolyMouseEvent) => {
      this.resetPolylineStyle(polyline);
    });

    setTimeout(() => {
      google.maps.event.clearListeners(polyline, 'click');
      this.addClickListener(polyline, listener);
    });
  }

  addClickListener(polyline: google.maps.Polyline, mouseOutListener: google.maps.MapsEventListener | null) {
    polyline.addListener("click", (event: google.maps.PolyMouseEvent) => {
      const data = polyline.get("data");

      // Se tab "Monitoraggio del traffico" attivo: il click sulla polyline fa scorrere la lista sul risultato corrispondente
      if(this.activeTabIndex() === 0) {
        this.scrollToStreetInList(data);

        return;
      }

      const polylineBounds = this.getPolylineBounds(polyline);

      // Reimpostazione stile di default polyline (se selezionata in precedenza)
      if(this.selectedStreet()) {
        const currentSelectedPolyline = this.selectedStreet()?.polyline;

        if(currentSelectedPolyline) {
          this.resetPolylineStyle(currentSelectedPolyline);

          // Riattivazione listener "mouseout"
          this.addMouseOutListener(currentSelectedPolyline);
        }
      }

      this.selectStreet(data);

      this.googleMap.fitBounds(polylineBounds);

      // Rimozione temporanea listener "mouseout"
      if(mouseOutListener) {
        mouseOutListener.remove();
        mouseOutListener = null;
      }

      this.setSelectedPolylineStile(polyline);
    });
  }

  // Ricerca la card HTML tramite ID e scorre fino al risultato
  scrollToStreetInList(street: any): void {
    // Utilizza l'ID fittizio per gli eventi risultato di una fusione, altrimenti il routeId originale
    const elementId = street.uniqueId || street.routeId;
    const element = document.getElementById(elementId);

    if(element) {
      // Scorrimento fluido
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Aggiunge un lampo visivo per far notare all'utente quale tratta è stata cliccata
      element.classList.add('highlight-pulse');
      setTimeout(() => {
        element.classList.remove('highlight-pulse');
      }, 1000);
    }
  }

  getPolylineBounds(polyline: google.maps.Polyline) {
    let bounds = new google.maps.LatLngBounds();
    const path = polyline.getPath();

    path.forEach((latLng: google.maps.LatLng) => {
      bounds.extend(latLng);
    });

    return bounds;
  }

  selectStreet(street: Street, highlightPolyline?: boolean) {
    // Sicurezza aggiuntiva: usciamo se per qualche motivo viene chiamato nel tab 0
    if(this.activeTabIndex() === 0) return;

    this.disableStreetView();
    this.selectedStreet.set(street);

    const polyline = street["polyline"];
    const polylineBounds = this.getPolylineBounds(polyline);

    // Nascondiamo tutte le polylines e accendiamo SOLO quella della strada cliccata
    this.hidePolylines();
    polyline.setMap(this.googleMap);

    if(highlightPolyline) {
      this.setSelectedPolylineStile(polyline);
    }

    this.googleMap.fitBounds(polylineBounds);

    // La prima volta che si apre il dettaglio vengono impostati dei valori di default:
    // - Periodo 1: due settimane fa
    // - Periodo 2: una settimana fa
    // - Intervallo: 7 giorni
    if(!this.period1Start) {
      let d1 = new Date();

      d1.setDate(d1.getDate() - 14);
      this.period1Start = this.formatDateValue(d1);
    }

    if(!this.period2Start) {
      let d2 = new Date();

      d2.setDate(d2.getDate() - 7);
      this.period2Start = this.formatDateValue(d2);
    }

    // Caricamento dei dati nel grafico (utilizzando i valori di default)
    this.loadComparisonData();

    // Diciamo alla view che abbiamo finito di caricare lo storico
    this.fetchSelectedStreetDataCompleted.set(true);
  }

  private formatDateValue(date: Date): string {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // Carica i dati ed elabora il grafico comparativo
  public loadComparisonData(): void {
    const routeId = this.selectedStreet()?.routeId;

    if(!routeId) {
      return;
    }

    // Controlla l'eventuale sovrapposizione di date
    this.dateOverlapError = ""; // Reset errore

    const p1StartMs = new Date(this.period1Start + "T00:00:00Z").getTime();
    const p2StartMs = new Date(this.period2Start + "T00:00:00Z").getTime();
    const intervalMs = this.intervalDays * 86400000;
    const p1EndMs = p1StartMs + intervalMs;
    const p2EndMs = p2StartMs + intervalMs;

    // Se l'inizio di un intervallo è minore della fine dell'altro i due intervalli si sovrappongono (e viceversa)
    if(p1StartMs < p2EndMs && p2StartMs < p1EndMs) {
      this.dateOverlapError = "Gli intervalli selezionati si sovrappongono.\nSeleziona date differenti, oppure riduci il numero di giorni.";

      return;
    }

    // Svuotamento del grafico per mostrare il caricamento
    this.lineChartData.datasets = [];
    this.chart?.update();
    this.cdr.detectChanges();

    const requestParameters = {
      routeId: routeId,
      startDate1: this.period1Start,
      startDate2: this.period2Start,
      intervalDays: this.intervalDays
    };

    this.streetService.getStreetHistoryCompareData(requestParameters).then((response: any) => {
      try {
        if(response) {
          const data1 = response.startDate1 || [];
          const data2 = response.startDate2 || [];

          // Timestamp "Zero" (inizio dei due periodi selezionati)
          // Affinchè la data venga letta senza fusi orari viene concatenata la stringa "T00:00:00Z"
          const p1StartMs = new Date(this.period1Start + "T00:00:00Z").getTime();
          const p2StartMs = new Date(this.period2Start + "T00:00:00Z").getTime();
          const timeMap = new Set<number>();
          const map1 = new Map<number, any>();
          const map2 = new Map<number, any>();

          // Mappa i tempi calcolando i millisecondi trascorsi dall'inizio del primo periodo
          data1.forEach((item: any) => {
            const relTime = new Date(item.recordHour).getTime() - p1StartMs;

            timeMap.add(relTime);
            map1.set(relTime, item);
          });

          // Mappa i tempi calcolando i millisecondi trascorsi dall'inizio del secondo periodo
          data2.forEach((item: any) => {
            const relTime = new Date(item.recordHour).getTime() - p2StartMs;

            timeMap.add(relTime);
            map2.set(relTime, item);
          });

          // Ordinamento della "linea del tempo relativa" in ordine crescente
          const sortedRelativeTimes = Array.from(timeMap).sort((a, b) => a - b);
          const labels: string[] = [];
          const ds1Dur: (number | null)[] = [];
          const ds1Stat: (number | null)[] = [];
          const ds1Orig: (string | null)[] = [];  // Array per le date originali Periodo 1
          const ds2Dur: (number | null)[] = [];
          const ds2Stat: (number | null)[] = [];
          const ds2Orig: (string | null)[] = [];  // Array per le date originali Periodo 2

          sortedRelativeTimes.forEach(relTimeMs => {
            // Calcolo etichetta semplificata per l'asse X
            const days = Math.floor(relTimeMs / 86400000) + 1;
            // Viene utilizzata una data fittizia per estrarre ore/minuti puliti in base al fuso orario
            const virtualDate = new Date(p1StartMs + relTimeMs);
            // Vengono utilizzati getUTCHours e getUTCMinutes per estrarre il valore esatto di data/ora
            const hours = virtualDate.getUTCHours().toString().padStart(2, '0');
            const minutes = virtualDate.getUTCMinutes().toString().padStart(2, '0');

            labels.push(`Giorno ${days} - ${hours}:${minutes}`);

            // Estrazione dati Periodo 1
            const item1 = map1.get(relTimeMs);

            ds1Dur.push(item1 ? item1.durationInSeconds : null);
            ds1Stat.push(item1 ? item1.staticDurationInSeconds : null);
            ds1Orig.push(item1 ? item1.recordHour : null);

            // Estrazione dati Periodo 2
            const item2 = map2.get(relTimeMs);

            ds2Dur.push(item2 ? item2.durationInSeconds : null);
            ds2Stat.push(item2 ? item2.staticDurationInSeconds : null);
            ds2Orig.push(item2 ? item2.recordHour : null);
          });

          this.lineChartData.labels = labels;

          // Popolamento dinamico dei 4 dataset
          this.lineChartData.datasets = [
            {
              data: ds1Dur,
              label: 'Traffico (Periodo 1)',
              borderColor: 'rgb(234, 67, 53)',  // Rosso
              backgroundColor: 'rgba(234, 67, 53, 0.1)',
              fill: false,
              tension: 0.3,
              borderWidth: 2,
              spanGaps: true, // Inserisce una linea continua ignorando eventuali valori 'null' intermedi
              originalDates: ds1Orig
            } as any,
            {
              data: ds1Stat,
              label: 'Statico (Periodo 1)',
              borderColor: 'rgba(234, 67, 53, 0.4)',  // Rosso sbiadito/tratteggiato
              borderDash: [5, 5],
              fill: false,
              tension: 0.3,
              borderWidth: 1,
              spanGaps: true,
              originalDates: ds1Orig,
              hidden: true, // Per il momento l'informazione relativa al tempo "Statico" non viene visualizzata all'interno del grafico
            } as any,
            {
              data: ds2Dur,
              label: 'Traffico (Periodo 2)',
              borderColor: 'rgb(66, 133, 244)', // Blu
              backgroundColor: 'rgba(66, 133, 244, 0.1)',
              fill: false,
              tension: 0.3,
              borderWidth: 2,
              spanGaps: true,
              originalDates: ds2Orig
            } as any,
            {
              data: ds2Stat,
              label: 'Statico (Periodo 2)',
              borderColor: 'rgba(66, 133, 244, 0.4)', // Blu sbiadito/tratteggiato
              borderDash: [5, 5],
              fill: false,
              tension: 0.3,
              borderWidth: 1,
              spanGaps: true,
              originalDates: ds2Orig,
              hidden: true, // Per il momento l'informazione relativa al tempo "Statico" non viene visualizzata all'interno del grafico
            } as any
          ];

          this.chart?.update();
          this.cdr.detectChanges();
        }
      } catch(error) {
        console.error("Errore parsing confronto:", error);
      }
    });
  }

  // Esegue lo zoom della mappa in corrispondenza del segmento di traffico alla selezione del risultati nella lista
  zoomToStreet(street: any): void {
    if (!this.googleMap || !street.polyline) return;

    // Calcola il perimetro (bounds) della specifica polyline
    const bounds = this.getPolylineBounds(street.polyline);

    // Centra e zooma la mappa per inquadrare perfettamente la coda
    this.googleMap.fitBounds(bounds);
  }

  // Trova l'inizio e la fine del segmento con traffico intenso e restituisce la stringa di avviso per ogni singolo sotto-segmento
  private getEttometricaTextForSegment(jamCoords: google.maps.LatLng[]): string | null {
    if(jamCoords.length === 0 || this.ettometricaFeatures.length === 0) {
      return null;
    }

    const startPoint = jamCoords.at(0)!;  // Il "!" finale indica a TypeScript che i valori sono sicuramente presenti (mai undefined)
    const endPoint = jamCoords.at(-1)!;
    const startMarker = this.findClosestEttometrica(startPoint);  // Il metodo "findClosestEttometrica" accetta solo LatLng (non undefined)
    const endMarker = this.findClosestEttometrica(endPoint);

    if(startMarker && endMarker) {
      // Usilizza il campo "Name" recuperato dal layer "Ettometrica"
      const startName = startMarker.Name;
      const endName = endMarker.Name;

      return `Dal km ${startName} al km ${endName}`;  // Ad esempio: "Dal km 280+800 al km 285+400"
    }

    return null;
  }

  // Trova il punto ettometrico geograficamente più vicino alla coordinata fornita
  private findClosestEttometrica(point: google.maps.LatLng): any {
    let minDistance = Infinity;
    let closestFeatureProps = null;

    for(const feature of this.ettometricaFeatures) {
      if(feature.geometry?.type === 'Point') {
        const coords = feature.geometry.coordinates;
        const markerLatLng = new google.maps.LatLng(coords[1], coords[0]);
        const distance = google.maps.geometry.spherical.computeDistanceBetween(point, markerLatLng);  // Calcola la distanza in linea d'aria tra l'inizio della coda e il cartello ettometrico

        if(distance < minDistance) {
          minDistance = distance;
          closestFeatureProps = feature.properties;
        }
      }
    }

    // Restituisce il dato solo se il cartello più vicino dista meno di 100 metri dalle coordinate di inizio/fine tratta con traffico
    return minDistance < 100 ? closestFeatureProps : null;
  }

  hidePolylines() {
    this.polylinesArray.forEach(polyline => {
      polyline.setMap(null);
    });

    this.globalTrafficSegmentsPolylines.forEach(polyline => {
      polyline.setMap(null);
    });
  }

  showPolylines() {
    this.streets.forEach(street => {
      if(street.isVisible && street.polyline) {
        street.polyline.setMap(this.googleMap);
      }
    });

    this.globalTrafficSegmentsPolylines.forEach(polyline => {
      polyline.setMap(this.googleMap);
    });

    this.fitBoundsToVisibleStreets();
  }

  formatLength(meters: number): string {
    if(meters >= 1000) {
      const formatterKm = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        style: 'unit',
        unit: 'kilometer',
        unitDisplay: 'short'
      });

      return formatterKm.format(meters / 1000);
    } else {
      const formatterM = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
        style: 'unit',
        unit: 'meter',
        unitDisplay: 'short'
      });

      return formatterM.format(meters);
    }
  }

  fromSecondsToMinutes(time: number): string {
    const minutes = Math.floor(time / 60).toString().padStart(2, '0');
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');

    return `${minutes}m ${seconds}s`;
  }

  backToList() {
    this.fetchSelectedStreetDataCompleted.set(null);
    this.disableStreetView();

    this.selectedStreet.set(null);

    // Ripristiniamo la mappa
    this.showPolylines();

    if(this.activeTabIndex() === 0) {
      this.startTrafficUpdates();
    }
  }

  setSelectedPolylineStile(polyline: google.maps.Polyline) {
    polyline.setOptions({
      strokeWeight: this.defaultSelectedPolylineStrokeWeight
    });
  }

  resetPolylineStyle(polyline: google.maps.Polyline) {
    polyline.setOptions({
      strokeWeight: this.defaultPolylineStrokeWeight
    });
  }

  mouseOverStreet(street: Street) {
    const polyline = street["polyline"];

    this.setSelectedPolylineStile(polyline);
  }

  mouseOutStreet(street: Street) {
    const polyline = street["polyline"];

    this.resetPolylineStyle(polyline);
  }

  disableStreetView() {
    if(this.streetViewPanorama.getVisible()) {
      this.streetViewPanorama.setVisible(false);
    }
  }

  // Metodo per rimuovere i marker di traffico dalla mappa
  private clearTrafficMarkers(): void {
    for(const marker of this.trafficMarkers) {
      marker.map = null;
    }
    this.trafficMarkers.length = 0; // Svuota l'array
  }

  // Metodo per disegnare il punto rosso in corrispondenza delle polyline su mappa (traffico intenso)
  private async drawTrafficMarkers(): Promise<void> {
    this.clearTrafficMarkers();

    if(this.activeTabIndex() !== 0 || this.visibleStreets.length === 0) return;

    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

    for(const street of this.visibleStreets) {
      const jamPaths: google.maps.LatLng[][] = street["trafficJamPaths"];

      if(!jamPaths || jamPaths.length === 0) continue;

      jamPaths.forEach((pathCoordinates) => {
        if(pathCoordinates.length === 0) return;

        const midIndex = Math.floor(pathCoordinates.length / 2);
        const midPoint = pathCoordinates[midIndex];

        const wrapperElement = document.createElement('div');
        wrapperElement.className = 'traffic-info';
        wrapperElement.style.pointerEvents = 'none';

        const dotA = document.createElement('span');
        dotA.className = 'traffic-dot-a intenso';
        const dotB = document.createElement('span');
        dotB.className = 'traffic-dot-b intenso';

        wrapperElement.appendChild(dotA);
        wrapperElement.appendChild(dotB);

        // NUOVO: Il titolo del Marker (tooltip al passaggio del mouse)
        // è esattamente il displayName dell'oggetto (es. "Dal km 2+100 al km 1+800")
        const marker = new AdvancedMarkerElement({
          map: this.googleMap,
          position: midPoint,
          content: wrapperElement,
          title: street.displayName
        });

        this.trafficMarkers.push(marker);
      });
    }
  }

  // Gestione del cambio di Tab (Monitoraggio / Analisi)
  onTabChange(index: number) {
    this.activeTabIndex.set(index);

    if(index === 0) {
      // Tab 0: Monitoraggio
      this.startTrafficUpdates();
    } else {
      // Tab 1: Analisi
      this.stopTrafficUpdates();
      this.resetAllStreetsToDefault();
      this.clearDisruptionMarkers();
    }

    this.updatePolylinesVisibility();
    this.drawTrafficMarkers();
  }

  // Elaborazione dei dati di traffico completi di geometrie + fusione segmenti e creazione "eventi" traffico
  private processTrafficDataWithGeometries(trafficResponse: any[]): void {
    this.resetAllStreetsToDefault();
    this.visibleStreets = [];

    if(!trafficResponse || trafficResponse.length === 0) {
      this.updatePolylinesVisibility(!this.isFirstTrafficLoad); // Passa true se non è il primo caricamento
      this.isFirstTrafficLoad = false;
      this.drawTrafficMarkers();
      this.cdr.detectChanges();
      return;
    }

    // --- NUOVO: Mappa per salvare i tempi globali univoci per ogni strada ---
    const routeTimesMap = new Map<string, { dur: number, stat: number, add: number }>();

    let rawSegments: any[] = [];
    for(const route of trafficResponse) {
      // Salviamo i tempi una sola volta associandoli al routeId
      const dur = route.durationInSeconds || 0;
      const stat = route.staticDurationInSeconds || 0;
      routeTimesMap.set(route.selectedRouteId, {
        dur: dur,
        stat: stat,
        add: dur - stat
      });

      if(route.segments && route.segments.length > 0) {
        const jams = route.segments.filter((s: any) => s.speed === "TRAFFIC_JAM" || s.speed === "TRAFFIC_JAM_HEAVY");
        for(const jam of jams) {
          const coords = this.extractCoordsFromGeoData(jam.geometryConverted);
          if(coords.length > 0) {
            rawSegments.push({
              routeId: route.selectedRouteId,
              displayName: route.displayName,
              coords: coords
              // Rimosso il salvataggio dei tempi qui, causava duplicazioni!
            });
          }
        }
      }
    }

    let mergedSegments = this.mergeContiguousSegments(rawSegments);
    let eventIndex = 0;

    for(const ms of mergedSegments) {
      const totalLength = google.maps.geometry.spherical.computeLength(ms.coords);
      const displayNamesArray = Array.from(ms.displayNames);

      const polyline = new google.maps.Polyline({
        path: ms.coords,
        geodesic: true,
        strokeColor: this.color_trafficJam,
        strokeOpacity: 1,
        strokeWeight: 4,
        zIndex: 2,
        map: this.googleMap
      });

      this.globalTrafficSegmentsPolylines.push(polyline);

      // --- NUOVO: Calcolo corretto dei tempi in base ai routeId univoci ---
      let totalDur = 0;
      let totalStat = 0;
      let totalAdd = 0;

      // Essendo routeIds un Set, se ci sono 3 segmenti della "144", ciclerà UNA sola volta!
      ms.routeIds.forEach((id: any) => {
        const times = routeTimesMap.get(id as string);
        if(times) {
          totalDur += times.dur;
          totalStat += times.stat;
          totalAdd += times.add;
        }
      });

      const textForSegment = this.getEttometricaTextForSegment(ms.coords);
      const mainName = textForSegment || displayNamesArray.join(' ➔ ');

      const virtualStreet: any = {
        uniqueId: `traffic-event-${eventIndex++}`,
        routeId: Array.from(ms.routeIds).join(','),
        displayName: mainName,
        traffic: "TRAFFIC_JAM",
        trafficDescription: this.trafficDescription_trafficJam,
        additionalTimeInSeconds: totalAdd,
        additionalTime: this.fromSecondsToMinutes(totalAdd),
        trafficJamLengthText: this.formatLength(totalLength),
        trafficJamPaths: [ms.coords],
        isVisible: true,
        polyline: polyline
      };

      polyline.setValues({ data: virtualStreet });
      this.addMouseOverListener(polyline);
      this.addMouseOutListener(polyline);

      this.visibleStreets.push(virtualStreet);
    }

    this.sortStreetsArrayByAdditionalTime(this.visibleStreets);
    this.updatePolylinesVisibility(!this.isFirstTrafficLoad);
    this.isFirstTrafficLoad = false;
    this.drawTrafficMarkers();
    this.cdr.detectChanges();
  }

  // Helper: Estrae in modo pulito le coordinate
  private extractCoordsFromGeoData(geoData: any): google.maps.LatLng[] {
    let coords: google.maps.LatLng[] = [];
    if(!geoData?.coordinates || !Array.isArray(geoData.coordinates)) return coords;

    if(geoData.type === "LineString") {
      coords = geoData.coordinates.map((c: any) => new google.maps.LatLng(c.lat, c.lng));
    } else if(geoData.type === "MultiLineString") {
      geoData.coordinates.forEach((line: any[]) => {
        coords.push(...line.map((c: any) => new google.maps.LatLng(c.lat, c.lng)));
      });
    }
    return coords;
  }

  // Algoritmo di fusione dei "segments": unisce i segmenti la cui fine coincide con l'inizio di un altro (con tolleranza di 2 metri)
  private mergeContiguousSegments(segments: any[]): any[] {
    const pool = segments.map(s => ({
      routeIds: new Set([s.routeId]),
      displayNames: new Set([s.displayName]),
      coords: [...s.coords]
    }));

    let hasMerged = true;

    // Continua a ciclare finché il metodo di supporto restituisce 'true' (cioè finché trova segmenti da unire)
    while(hasMerged) {
      hasMerged = this.processSingleMerge(pool);
    }

    return pool;
  }

  // Cerca la prima fusione possibile e unisce i dati
  private processSingleMerge(pool: any[]): boolean {
    for(let i = 0; i < pool.length; i++) {
      for(let j = 0; j < pool.length; j++) {
        if(i === j) continue;

        const segA = pool[i];
        const segB = pool[j];

        const endA = segA.coords.at(-1)!;
        const startB = segB.coords[0];

        const dist = google.maps.geometry.spherical.computeDistanceBetween(endA, startB);

        if(dist <= 2) {
          // Unione delle coordinate, ID e nomi
          segA.coords.push(...segB.coords.slice(1));
          segB.routeIds.forEach((id: string) => segA.routeIds.add(id));
          segB.displayNames.forEach((name: string) => segA.displayNames.add(name));

          // Rimuove l'elemento B dall'array perché è stato assorbito in A
          pool.splice(j, 1);

          // Uscita anticipata (Early return): abbiamo fuso, restituiamo true per far ripartire il while
          return true;
        }
      }
    }

    // Se i cicli finiscono senza trovare vicini, restituisce false bloccando il while
    return false;
  }

  // Sostituisce il vecchio metodo di ordinamento, così possiamo passargli l'array che vogliamo
  private sortStreetsArrayByAdditionalTime(arrayToSort: any[]) {
    arrayToSort.sort((a, b) => {
      const valA = a.additionalTimeInSeconds || 0;
      const valB = b.additionalTimeInSeconds || 0;
      return valB - valA;
    });
  }

  // Metodo per rimuovere i marker delle interruzioni dalla mappa
  private clearDisruptionMarkers(): void {
    for(const marker of this.disruptionMarkers) {
      marker.map = null;
    }

    this.disruptionMarkers.length = 0;

    for(const polyline of this.disruptionPolylines) {
      polyline.setMap(null);
    }

    this.disruptionPolylines.length = 0;
  }

  // Nasconde/mostra le interruzioni stradali su mappa (marker e polyline)
  public toggleDisruptionsVisibility(): void {
    this.showDisruptions.set(!this.showDisruptions());

    const targetMap = this.showDisruptions() ? this.googleMap : null;

    for(const marker of this.disruptionMarkers) {
      marker.map = targetMap;
    }

    for(const polyline of this.disruptionPolylines) {
      polyline.setMap(targetMap);
    }
  }

  // Metodo per disegnare i marker delle interruzioni sulla mappa
  private async drawDisruptionMarkers(disruptions: any[]): Promise<void> {
    this.clearDisruptionMarkers();

    if(this.activeTabIndex() !== 0 || !disruptions || disruptions.length === 0) {
      return;
    }

    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

    for(const disruption of disruptions) {
      this.processSingleDisruption(disruption, AdvancedMarkerElement);
    }
  }

  private processSingleDisruption(disruption: any, AdvancedMarkerElement: any): void {
    if(!disruption.stretches || !Array.isArray(disruption.stretches)) {
      return;
    }

    for(const stretch of disruption.stretches) {
      this.createDisruptionPolyline(stretch);
      this.createDisruptionMarker(stretch, disruption, AdvancedMarkerElement);
    }
  }

  private createDisruptionPolyline(stretch: any): void {
    if(!stretch.positions || !Array.isArray(stretch.positions) || stretch.positions.length <= 1) {
      return;
    }

    const path = stretch.positions.map((p: any) => new google.maps.LatLng(p.latitude, p.longitude));
    const polyline = new google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: '#202124',
      strokeOpacity: 0.4,
      strokeWeight: 6,
      zIndex: 3,  // Disegnata sopra il traffico (zIndex 2) e la strada (zIndex 1)
      map: this.showDisruptions() ? this.googleMap : null
    });

    this.disruptionPolylines.push(polyline);
  }

  private createDisruptionMarker(stretch: any, disruption: any, AdvancedMarkerElement: any): void {
    if(!stretch.centroid?.latitude || !stretch.centroid?.longitude) {
      return;
    }

    const position = new google.maps.LatLng(stretch.centroid.latitude, stretch.centroid.longitude);
    const disruptionConfig = this.getDisruptionConfig(disruption.disruptionType); // Assegnazione stile in base alla tipologia

    // Recupero del km dal layer "Ettometrica"
    const closestEttometrica = this.findClosestEttometrica(position);
    let titleText = disruptionConfig.label;

    if(closestEttometrica?.Name) {
      titleText += ` nei pressi del km ${closestEttometrica.Name}`;
    } else if(disruption.displayName) {
      titleText += `: ${disruption.displayName}`;
    }

    const imgElement = document.createElement('img');

    imgElement.src = disruptionConfig.iconUrl;
    imgElement.alt = disruptionConfig.label;
    imgElement.style.width = '24px';
    imgElement.style.height = '24px';
    imgElement.style.pointerEvents = 'none';

    const marker = new AdvancedMarkerElement({
      map: this.showDisruptions() ? this.googleMap : null,
      position: position,
      content: imgElement,
      title: titleText
    });

    this.disruptionMarkers.push(marker);
  }

  // Metodo per mappare le tipologie di interruzioni
  private getDisruptionConfig(type: string): { iconUrl: string, label: string } {
    const typeUpper = type?.toUpperCase() || '';

    switch(typeUpper) {
      case 'RESPONSE_DISRUPTION_TYPE_CONSTRUCTION':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/construction_card_v2.svg',
          label: 'Lavori in corso'
        };
      case 'RESPONSE_DISRUPTION_TYPE_CRASH':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/crash_card.svg',
          label: 'Incidente'
        };
      case 'RESPONSE_DISRUPTION_TYPE_FLOOD':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/flood_card_v2.svg',
          label: 'Allagamento'
        };
      case 'RESPONSE_DISRUPTION_TYPE_FOG':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/fog_card_v2.svg',
          label: 'Nebbia'
        };
      case 'RESPONSE_DISRUPTION_TYPE_JAM':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic_report/svg/ic_qu_medium_traffic.svg',
          label: 'Traffico'
        };
      case 'RESPONSE_DISRUPTION_TYPE_LANE_CLOSURE':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/lane_closure_card.svg',
          label: 'Corsia chiusa'
        };
      case 'RESPONSE_DISRUPTION_TYPE_OBJECT_ON_ROAD':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/object_on_road_card_v2.svg',
          label: 'Oggetto su strada'
        };
      case 'RESPONSE_DISRUPTION_TYPE_ROAD_CLOSED':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/road_closed_card.svg',
          label: 'Strada chiusa'
        };
      case 'RESPONSE_DISRUPTION_TYPE_SLIPPERY_ROAD':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/ice_card.svg',
          label: 'Strada scivolosa'
        };
      case 'RESPONSE_DISRUPTION_TYPE_STALLED_VEHICLE':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/stalled_vehicle_card.svg',
          label: 'Veicolo in panne'
        };
      case 'RESPONSE_DISRUPTION_TYPE_SUSPECTED_ROAD_CLOSED':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/suspected_road_closure_card.svg',
          label: 'Presunta chiusura stradale'
        };
      case 'RESPONSE_DISRUPTION_TYPE_UNPLOWED_ROAD':
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/ice_card.svg',
          label: 'Strada non spazzata'
        };
      default:
        // Icona di fallback generica
        return {
          iconUrl: 'https://maps.gstatic.com/mapfiles/traffic/svg/suspected_road_closure_card.svg',
          label: 'Alert generico'
        };
    }
  }
}
